import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/supabase';
import useGameStore from '../lib/gameStore';
import Avatar from '../components/Avatar';
import AvatarUpload from '../components/AvatarUpload';
import { toast } from 'sonner';
import { useCoursePromo } from '../contexts/CoursePromoContext';

const Profile = () => {
  const navigate = useNavigate();
  const { profile: userProfile, setProfile } = useGameStore();
  const { checkTriggers } = useCoursePromo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customAvatar, setCustomAvatar] = useState(null);
  const [formData, setFormData] = useState({
    avatar_type: 'robot',
    avatar_color: '#FF6B6B',
    avatar_name: 'Automator',
  });
  const [userName, setUserName] = useState('');

  useEffect(() => {
    loadProfile();
    // Trigger: PROFILE_VISIT - cuando el usuario visita su perfil
    checkTriggers({
      type: 'PROFILE_VISIT',
      data: {}
    });
  }, [checkTriggers]);

  const loadProfile = async () => {
    try {
      // Get current user from Supabase auth
      const { user, error: userError } = await auth.getUser();

      if (userError || !user) {
        toast.error('Error al obtener usuario');
        navigate('/');
        return;
      }

      // Load profile from Supabase
      const { data: profileData, error: profileError } = await db.profile.get(user.id);

      if (profileError || !profileData) {
        toast.error('Error al cargar el perfil');
        return;
      }

      setProfile(profileData);
      // Get user name from profile (display_name), fallback to auth metadata, then avatar_name
      const displayName = profileData.display_name || user.user_metadata?.name || profileData.avatar_name || 'Usuario';
      setUserName(displayName);
      setFormData({
        avatar_type: profileData.avatar_type,
        avatar_color: profileData.avatar_color,
        avatar_name: profileData.avatar_name,
      });

      // Load avatar from Supabase Storage if exists
      if (profileData.avatar_url) {
        setCustomAvatar(profileData.avatar_url);
      }
    } catch (error) {
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();

      if (userError || !user) {
        toast.error('Error al obtener usuario');
        return;
      }

      // Update user metadata (name) in Supabase auth
      if (userName && userName.trim() !== '') {
        const { error: authUpdateError } = await auth.updateUser({
          data: { name: userName.trim() }
        });

        if (authUpdateError) {
          // Continue anyway to save to profile table
        }
      }

      // Update profile in Supabase (including display_name)
      const { data: updatedProfile, error: updateError } = await db.profile.update(user.id, {
        avatar_type: formData.avatar_type,
        avatar_color: formData.avatar_color,
        avatar_name: formData.avatar_name,
        display_name: userName && userName.trim() !== '' ? userName.trim() : null,
      });

      if (updateError) {
        throw updateError;
      }

      // Update global state with new profile
      if (updatedProfile) {
        setProfile(updatedProfile);
      }

      toast.success('Perfil actualizado exitosamente');

      // Reload profile to get fresh auth user data with updated name
      await loadProfile();
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (avatarUrl) => {
    setCustomAvatar(avatarUrl);

    // Save avatar URL to database immediately (null if removed)
    try {
      const { user, error: userError } = await auth.getUser();
      if (userError || !user) {
        return;
      }

      const { data: updatedProfile, error: updateError } = await db.profile.update(user.id, {
        avatar_url: avatarUrl,
      });

      if (updateError) {
        return;
      }

      if (updatedProfile) {
        // Update global state with new avatar URL
        setProfile(updatedProfile);
      }
    } catch (error) {
      // Silent fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* Header */}
      <header className="glass border-b border-white/10 sticky top-0 z-50 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-text-secondary hover:text-primary transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary">Mi Perfil</h1>
              <p className="text-sm text-text-secondary">Personaliza tu avatar y configuración</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Avatar Upload Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass p-6 rounded-card"
          >
            <h2 className="text-xl font-heading font-bold text-text-primary mb-6">Imagen de Avatar</h2>
            <AvatarUpload
              currentAvatar={customAvatar}
              onImageUpload={handleImageUpload}
              avatarType={formData.avatar_type}
              avatarColor={formData.avatar_color}
            />
          </motion.div>

          {/* Avatar Customization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass p-6 rounded-card"
          >
            <h2 className="text-xl font-heading font-bold text-text-primary mb-6">
              Personalización de Avatar
            </h2>

            <div className="space-y-6">
              {/* User Name (editable) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Nombre</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Ingresa tu nombre"
                  className="w-full bg-background-subtle/50 border border-slate-700 focus:border-primary text-white rounded-lg h-12 px-4 outline-none transition-colors"
                />
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Vista Previa</label>
                <div className="flex justify-center p-6 glass rounded-lg">
                  <Avatar
                    type={formData.avatar_type}
                    color={formData.avatar_color}
                    size="xl"
                    animate={false}
                    customImage={customAvatar}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Save Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full btn-neon py-4 rounded-lg font-heading font-bold flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              <Save className="w-6 h-6" />
              <span>{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;